import React, { useContext } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import AuthContext from "../contexts/AuthContext";
import { useSubscription } from "../hooks/useSubscription";
import SubscriptionGuard from "./SubscriptionGuard";

const PrivateRoute = ({
  children,
  requireSubscription = true,
  requireActiveSubscription = false,
  subscriptionFeature = "general",
  bypassSubscriptionCheck = false,
}) => {
  const { auth } = useContext(AuthContext);
  const location = useLocation();
  const subscription = useSubscription();

  // Never redirect the rabbit route
  if (location.pathname === "/rabbit") {
    return children;
  }

  // Authentication loading state
  if (auth.loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "100vh" }}
      >
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  // Check authentication first
  if (!auth.isAuthenticated) {
    return <Navigate to="/login" />;
  }

  // Admin users bypass subscription checks
  if (auth.isAdmin || bypassSubscriptionCheck) {
    return children;
  }

  // If subscription checking is disabled for this route, just return children
  if (!requireSubscription) {
    return children;
  }

  // Subscription loading state (only show if we've been loading for a bit)
  if (subscription.loading && !subscription.subscription) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "200px" }}
      >
        <Spinner animation="border" role="status" size="sm">
          <span className="visually-hidden">Checking subscription...</span>
        </Spinner>
        <span className="ms-2 text-muted">Checking subscription status...</span>
      </div>
    );
  }

  // Check subscription access with SubscriptionGuard
  return (
    <SubscriptionGuard
      feature={subscriptionFeature}
      requireActive={requireActiveSubscription}
      showTrialPrompt={true}
      showSubscriptionStatus={true}
      customMessage={
        requireActiveSubscription
          ? "This feature requires an active subscription."
          : "Please start a trial or subscribe to access this feature."
      }
    >
      {children}
    </SubscriptionGuard>
  );
};

export default PrivateRoute;
